import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { authService, userService } from '../../../../services/apiService';
import { getDisplayNameForUI, getDisplayRole, getProfileDisplayEmail, getProfileDisplayName, getProfileDisplayPhone } from '../../../../utils/displayName';
import { FaUser, FaEnvelope, FaPhone, FaSave, FaLock } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', phoneCode: '+91' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: getProfileDisplayName(user),
        email: getProfileDisplayEmail(user),
        phone: getProfileDisplayPhone(user),
        phoneCode: user.phoneCode || '+91'
      });
    }
  }, [user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const userId = user?.id || user?._id;
    if (!userId) {
      toast.error('User not found');
      return;
    }
    setLoadingProfile(true);
    try {
      const res = await userService.updateUser(userId, {
        name: profile.name?.trim(),
        email: profile.email?.trim(),
        phone: (profile.phone || '').replace(/\D/g, '').slice(-10),
        phoneCode: profile.phoneCode || '+91'
      });
      const updated = res?.user || res?.data?.user || res;
      if (updated) {
        updateUser?.({ ...user, ...updated });
        toast.success('Profile updated successfully');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setLoadingPassword(true);
    try {
      await authService.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password updated successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">My Profile</h2>
        <p className="text-xs text-gray-600 mt-1">Manage your credentials and profile (Super Admin)</p>
      </div>

      <form onSubmit={handleSaveProfile} className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <div className="flex items-center gap-6 mb-6">
          <div className="w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center text-white text-3xl">
            <FaUser />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">{getDisplayNameForUI(user)}</h3>
            <p className="text-sm text-gray-600 capitalize">{getDisplayRole(user)}</p>
            {getProfileDisplayEmail(user) ? <p className="text-sm text-gray-600">{getProfileDisplayEmail(user)}</p> : null}
            {getProfileDisplayPhone(user) ? <p className="text-sm text-gray-600">{user?.phoneCode ? `${user.phoneCode} ${user.phone}` : `+91 ${user.phone}`}</p> : null}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaUser className="inline mr-2" /> Full Name
            </label>
            <input
              type="text"
              name="name"
              value={profile.name}
              onChange={handleProfileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder=""
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaEnvelope className="inline mr-2" /> Email
            </label>
            <input
              type="email"
              name="email"
              value={profile.email}
              onChange={handleProfileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder=""
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaPhone className="inline mr-2" /> Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={profile.phone}
              onChange={handleProfileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder=""
              maxLength="10"
            />
            <p className="text-xs text-gray-500 mt-1">Login number (e.g. 9087654321). You can change it here.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <input
              type="text"
              value={getDisplayRole(user) || ''}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={loadingProfile} className="btn-primary flex items-center gap-2">
            <FaSave /> {loadingProfile ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>

      <form onSubmit={handleChangePassword} className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FaLock /> Change Password
        </h3>
        <p className="text-xs text-gray-600 mb-4">
          Leave current password empty if you logged in with phone (9087654321) and have not set a password yet.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Leave empty if first time"
              autoComplete="current-password"
            />
          </div>
          <div />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password (min 6 characters)</label>
            <input
              type="password"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="New password"
              minLength={6}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={loadingPassword} className="btn-primary flex items-center gap-2">
            <FaLock /> {loadingPassword ? 'Updating...' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Profile;
